import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { staffApi } from "../api/staffApi";
import { staffTokenStore } from "../api/tokenStore";
import { StaffUser } from "../types/auth";

type AuthState = {
  user: StaffUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Ao montar, tenta uma sessão silenciosa via cookie de refresh — cobre o F5 na página.
  useEffect(() => {
    let cancelled = false;
    staffApi
      .post("/auth/staff/refresh")
      .then((res) => {
        if (cancelled) return;
        staffTokenStore.set(res.data.accessToken);
        setUser(res.data.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await staffApi.post("/auth/staff/login", { email, password });
    staffTokenStore.set(res.data.accessToken);
    setUser(res.data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await staffApi.post("/auth/staff/logout");
    } finally {
      staffTokenStore.set(null);
      setUser(null);
    }
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return ctx;
}
